// src/pages/manager/production/ManagerViewProduction.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import SuperAdminLayout from "../../../layouts/SuperAdminLayout";

const ViewProduction = () => {
  const superAdminId = JSON.parse(localStorage.getItem("user"))?.id;
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);

  // for edit dialog
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState({
    recordId: "",
    itemIndex: 0,
    date: "",
    product: "",
    unit: "",
    availableQty: 0,
    productDetails: null,
    secondaryQuantity: "",
    primaryQuantity: "",
  });
  const [products, setProducts] = useState([]);

  // fetch all production records
  const fetchRecords = async () => {
    const res = await api.get(`/api/production/get-all/${superAdminId}`);
    setRecords(res.data);
  };

  useEffect(() => {
    if (superAdminId) {
      fetchRecords();
      // fetch product list for dropdown
      api
        .get(`/api/products/get-all/${superAdminId}`)
        .then((r) => setProducts(r.data));
    }
  }, [superAdminId]);

  // track which ones have been converted
  const converted = JSON.parse(localStorage.getItem("converted") || "[]");

  // helper to recalc primary/secondary when one changes
  const handleItemChange = (key, value) => {
    setEditData((prev) => {
      const it = { ...prev };
      if (key === "product") {
        const pd = products.find((p) => p.productName === value);
        if (pd) {
          it.product = pd.productName;
          it.unit = pd.primaryUnit;
          it.availableQty = pd.closingQty;
          it.productDetails = pd;
          it.primaryQuantity = 0;
          it.secondaryQuantity = 0;
        }
      } else if (key === "primaryQuantity") {
        it.primaryQuantity = value;
        const secFactor = prev.productDetails?.secondaryQuantity || 1;
        it.secondaryQuantity = parseFloat((value / secFactor).toFixed(2));
      } else if (key === "secondaryQuantity") {
        it.secondaryQuantity = value;
        const secFactor = prev.productDetails?.secondaryQuantity || 1;
        it.primaryQuantity = parseFloat((value * secFactor).toFixed(2));
      }
      return it;
    });
  };

  // open edit dialog for a given row
  const handleOpenEdit = (row) => {
    setEditData({
      recordId: row.recordId,
      itemIndex: row.itemIndex,
      date: row.date,
      product: row.product,
      unit: row.unit,
      availableQty: row.availableQty,
      productDetails:
        products.find((p) => p.productName === row.product) || null,
      secondaryQuantity: row.secondaryQty,
      primaryQuantity: row.primaryQty,
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  // save edits
  const handleSaveEdit = async () => {
    const { recordId, itemIndex, date } = editData;
    const record = records.find((r) => r._id === recordId);
    const newItems = [...record.items];
    newItems[itemIndex] = {
      ...newItems[itemIndex],
      product: editData.product,
      unit: editData.unit,
      secondaryQuantity: Number(editData.secondaryQuantity),
      primaryQuantity: Number(editData.primaryQuantity),
    };
    await api.put(`/api/production/update/${recordId}`, {
      superAdminId,
      date,
      items: newItems,
    });
    setOpenEdit(false);
    fetchRecords();
  };

  // delete entire record
  const handleDelete = async (recordId) => {
    if (!window.confirm("Delete this production entry?")) return;
    await api.delete(`/api/production/delete/${recordId}`);
    fetchRecords();
  };

  // flatten records into rows
  const rows = records.flatMap((record) =>
    record.items.map((item, idx) => ({
      key: `${record._id}-${idx}`,
      recordId: record._id,
      date: new Date(record.date).toISOString().slice(0, 10),
      product: item.product,
      unit: item.unit,
      secondaryQty: item.secondaryQuantity,
      primaryQty: item.primaryQuantity,
      availableQty: item.availableQty ?? 0,
      itemIndex: idx,
    }))
  );

  return (
    <SuperAdminLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Edit Dialog */}
        <Dialog open={openEdit} onClose={handleCloseEdit}>
          <DialogTitle>Edit Production</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              type="date"
              label="Date"
              value={editData.date}
              onChange={(e) =>
                setEditData((d) => ({ ...d, date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Product"
              value={editData.product}
              onChange={(e) => handleItemChange("product", e.target.value)}
            >
              {products.map((p) => (
                <MenuItem key={p._id} value={p.productName}>
                  {p.productName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Unit"
              value={editData.unit}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Available Stock"
              value={editData.availableQty}
              InputProps={{ readOnly: true }}
            />
            <TextField
              type="number"
              label="Secondary Qty"
              value={editData.secondaryQuantity}
              onChange={(e) =>
                handleItemChange("secondaryQuantity", Number(e.target.value))
              }
            />
            <TextField
              type="number"
              label="Primary Qty"
              value={editData.primaryQuantity}
              onChange={(e) =>
                handleItemChange("primaryQuantity", Number(e.target.value))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h5">Production Records</Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/super-admin/production/create")}
          >
            + New Entry
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Secondary Qty</TableCell>
                <TableCell>Primary Qty</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.product}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell>{row.secondaryQty}</TableCell>
                  <TableCell>{row.primaryQty}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenEdit(row)}
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleDelete(row.recordId)}
                      sx={{ mr: 1 }}
                    >
                      Delete
                    </Button>
                    {!converted.includes(row.recordId) && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate("/super-admin/prodpchallan/create", {
                            state: {
                              production: records.find(
                                (r) => r._id === row.recordId
                              ),
                            },
                          })
                        }
                      >
                        Convert to P‑Challan
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </SuperAdminLayout>
  );
};

export default ViewProduction;
